/* Gift prices are owned by the Shopify Function, not client-side price edits.
   Checkout validation independently checks final spend and exactly one selected gift. */
(function () {
  'use strict';
  if (customElements.get('ama-tier-gifts')) return;
  let currentCart = null;
  let pending = null;
  let timer;
  let changed = false;
  let failed = false;
  let suppressObserver = false;
  let choicePending = false;
  let choiceTask = null;
  const root = () => window.Shopify?.routes?.root || '/';
  const widgets = () => document.querySelectorAll('ama-tier-gifts');
  function render(cart) {
    currentCart = cart;
    suppressObserver = true;
    widgets().forEach(widget => widget.render(cart));
    setTimeout(() => { suppressObserver = false; }, 0);
  }
  let configuration = null;
  try { configuration = JSON.parse(document.getElementById('ama-tier-gift-config')?.textContent || 'null'); } catch (_) {}
  const automatic = () => document.querySelector('ama-tier-gifts[data-automatic="true"]') || configuration?.enabled;
  const minimum = () => Number(configuration?.threshold || 5900);
  // Izprati can replace cart attributes during native-cart preparation. The
  // surviving marked gift line is also an authoritative saved choice.
  const selection = cart => String(cart.attributes?.ama_gift_choice || cart.items?.find(item => item.properties?._ama_tier_gift === configuration?.campaign)?.variant_id || configuration?.choice || '');
  async function cartRequest(path, body) {
    const response = await fetch(root() + path, {
      method: body ? 'POST' : 'GET',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || 'Cart unavailable');
    return data;
  }
  const isGift = item => Boolean(item.properties?._ama_tier_gift);
  const spend = cart => {
    if (!Array.isArray(cart.items)) return Math.max(0, Number(cart.total_price) || 0);
    // Some checkout integrations expose a total that includes a selected
    // shipping charge. Sum merchandise lines instead; never count delivery.
    const products = cart.items.filter(item => !isGift(item))
      .reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0);
    const orderDiscounts = (cart.cart_level_discount_applications || [])
      .reduce((sum, discount) => sum + (Number(discount.total_allocated_amount) || 0), 0);
    return Math.max(0, products - orderDiscounts);
  };
  async function reconcile(cart) {
    const module = automatic();
    if (!module || cart.currency !== 'EUR') return cart;
    const choices = configuration?.choices || [];
    for (let pass = 0; pass < 4; pass++) {
      const total = spend(cart);
      const desired = choices.filter(choice => choice.available && choice.variant && String(choice.variant) === selection(cart) && total >= minimum()).slice(0, 1);
      const kept = new Set();
      const updates = {};
      for (const item of cart.items.filter(isGift)) {
        const eligible = item.properties._ama_tier_gift === configuration.campaign && desired.some(choice => choice.variant === item.variant_id);
        const keep = eligible && !kept.has(item.variant_id);
        if (keep) kept.add(item.variant_id);
        if (!keep || item.quantity !== 1) updates[item.key] = keep ? 1 : 0;
      }
      if (Object.keys(updates).length) {
        cart = await cartRequest('cart/update.js', { updates });
        changed = true;
        continue;
      }
      const missing = desired.filter(tier => !kept.has(tier.variant));
      if (!missing.length) break;
      await cartRequest('cart/add.js', { items: missing.map(tier => ({ id: tier.variant, quantity: 1,
        properties: { _ama_tier_gift: configuration.campaign },
      })) });
      changed = true;
      cart = await cartRequest('cart.js');
      const charged = cart.items.filter(item => isGift(item) && item.final_line_price !== 0);
      if (charged.length) {
        // Fail safely: never leave an automatically added gift at a paid price.
        cart = await cartRequest('cart/update.js', { updates: Object.fromEntries(charged.map(item => [item.key, 0])) });
        throw new Error('Gift discount unavailable');
      }
    }
    return cart;
  }
  async function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    const pageParts = ['main-cart-items', 'main-cart-footer'].map(id => {
      const element = document.getElementById(id);
      return element && { element, section: element.dataset.id, wrapper: element.closest('.shopify-section') };
    }).filter(part => part?.section && part.wrapper);
    const ids = ['cart-icon-bubble', ...(drawer?.renderContents ? ['cart-drawer'] : []), ...pageParts.map(part => part.section)];
    const response = await fetch(root() + '?sections=' + ids.map(encodeURIComponent).join(','));
    if (!response.ok) return;
    const sections = await response.json();
    suppressObserver = true;
    if (drawer?.renderContents) drawer.renderContents({ sections }, false);
    for (const part of pageParts) {
      if (!sections[part.section]) continue;
      const note = part.wrapper.querySelector('textarea[name="note"]');
      const noteValue = note?.value;
      const parsed = new DOMParser().parseFromString(sections[part.section], 'text/html');
      part.wrapper.innerHTML = parsed.getElementById(part.wrapper.id)?.innerHTML || sections[part.section];
      const newNote = part.wrapper.querySelector('textarea[name="note"]');
      if (newNote && noteValue !== undefined) newNote.value = noteValue;
    }
    if (!drawer?.renderContents && sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const fresh = parsed.getElementById('cart-icon-bubble');
      if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
    }
    setTimeout(() => { suppressObserver = false; }, 0);
  }
  function refresh() {
    if (pending) return pending;
    pending = cartRequest('cart.js').then(reconcile).then(async cart => {
      failed = false;
      render(cart);
      if (changed) await refreshDrawer();
    }).catch(() => {
      failed = true;
      widgets().forEach(widget => {
        const message = widget.querySelector('.ama-tier-gifts__next');
        message.textContent = widget.dataset.locale === 'en' ? 'Gifts could not be updated. Please try again.' : 'Подаръците не успяха да се обновят. Моля, опитай отново.';
      });
    }).finally(() => { pending = null; changed = false; });
    return pending;
  }
  function schedule() { if (suppressObserver) return; clearTimeout(timer); timer = setTimeout(refresh, 100); }
  window.amaTierGiftsReady = refresh;
  async function chooseGift(variant) {
    if (choicePending || !configuration?.choices?.some(choice => choice.available && String(choice.variant) === String(variant))) return;
    choicePending = true;
    widgets().forEach(widget => widget.querySelectorAll('[data-gift-choice]').forEach(button => { button.disabled = true; }));
    try {
      if (pending) await pending;
      await cartRequest('cart/update.js', { attributes: { ama_gift_choice: String(variant) } });
      await refresh();
    } catch (_) {
      failed = true;
      widgets().forEach(widget => { widget.querySelector('.ama-tier-gifts__next').textContent = widget.dataset.locale === 'en' ? 'Could not save your gift. Please try again.' : 'Не успяхме да запазим подаръка. Опитай отново.'; });
    } finally {
      choicePending = false;
      if (currentCart && !failed) render(currentCart);
      else widgets().forEach(widget => widget.querySelectorAll('[data-gift-choice]').forEach(button => { button.disabled = button.dataset.available !== 'true'; }));
    }
  }
  function requireGiftChoice() {
    if (spend(currentCart) >= minimum() && configuration?.choices?.some(choice => choice.available) && !currentCart.items.some(isGift)) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.open) drawer.open();
      widgets().forEach(widget => { widget.querySelector('.ama-tier-gifts__next').textContent = widget.dataset.locale === 'en' ? 'Choose one of the two free gifts before continuing.' : 'Избери един от двата безплатни подаръка, преди да продължиш.'; });
      throw new Error('Избери подарък / Please choose a gift');
    }
  }
  // Izprati uses its own checkout modal, including the Draft Order path. Settle
  // the Shopify cart before it reads prices and refuse a stale gift snapshot.
  const giftSignature = cart => JSON.stringify((cart?.items || []).filter(isGift)
    .map(item => [item.variant_id, item.quantity, item.final_line_price]).sort());
  let popupAttempts = 0;
  const popupTimer = setInterval(() => {
    const popup = window.OctavaCheckoutPopup;
    if (popup && typeof popup.openModal === 'function' && !popup.amaTierGiftsInstalled) {
      popup.amaTierGiftsInstalled = true;
      const open = popup.openModal;
      const proceed = popup.proceedToCheckout;
      let openedGifts = '';
      popup.openModal = async function (...args) {
        if (automatic()) {
          if (choiceTask) await choiceTask;
          await refresh();
          if (failed) throw new Error('Подаръците не успяха да се обновят. Опитайте отново.');
          requireGiftChoice();
          openedGifts = giftSignature(currentCart);
        }
        return open.apply(this, args);
      };
      if (typeof proceed === 'function') popup.proceedToCheckout = async function (...args) {
        if (automatic()) {
          if (choiceTask) await choiceTask;
          await refresh();
          requireGiftChoice();
          if (failed || (openedGifts && openedGifts !== giftSignature(currentCart))) throw new Error('Подаръците са обновени. Затворете и отворете формата отново, за да видите актуалната поръчка.');
          // Leave the live courier quote and all sub-threshold orders alone.
          // Draft Order conversion loses the private gift line attribute. Only
          // hand off the original cart when the selected quote is explicitly
          // free, in EUR/Bulgaria, and the discounted merchandise earns a gift.
          const hasGifts = currentCart.items.some(isGift);
          if (hasGifts) {
            const form = args[0];
            const quote = form?.selectedDeliveryOption?.estimatedCost;
            const amount = quote?.amount;
            const free = amount !== undefined && amount !== null && String(amount).trim() !== '' && Number(amount) === 0;
            const euro = (quote?.currencyCode || form?.orderTotals?.currency) === 'EUR';
            const bulgaria = form?.countryCode === 'BG';
            if (spend(currentCart) < minimum() || Number(form?.orderTotals?.total_price) !== spend(currentCart) || !free || !euro || !bulgaria || currentCart.items.some(item => isGift(item) && item.final_line_price !== 0)) {
              throw new Error('Не успяхме да потвърдим безплатната доставка и подаръците. Затворете формата и опитайте отново.');
            }
            // Reuse only a postal code already returned for this selected
            // destination. Never invent a generic postcode for an office.
            if (['OFFICE', 'BOX'].includes(form.selectedDeliveryOption.methodType) && !form.pickupPointPostcode && /^\d{4}$/.test(String(form.destination?.postal_code || ''))) {
              form.pickupPointPostcode = String(form.destination.postal_code);
            }
            const originalFlow = this.effectiveCheckoutFlow;
            this.effectiveCheckoutFlow = 'prefilled_cart';
            try { return await proceed.apply(this, args); }
            finally { this.effectiveCheckoutFlow = originalFlow; }
          }
        }
        return proceed.apply(this, args);
      };
      clearInterval(popupTimer);
    }
    // A slow third-party script must not miss the gift-safe handoff hook.
    if (++popupAttempts >= 120 && !automatic()) clearInterval(popupTimer);
  }, 250);
  class TierGifts extends HTMLElement {
    connectedCallback() {
      if (!this.choiceListenerInstalled) {
        this.choiceListenerInstalled = true;
        this.addEventListener('click', event => {
          const button = event.target.closest('[data-gift-choice]');
          if (button && this.contains(button)) choiceTask = chooseGift(button.dataset.giftChoice);
        });
      }
      this.render(currentCart || { total_price: Number(this.dataset.cartTotal), currency: this.dataset.currency });
      schedule();
    }
    render(cart) {
      const english = this.dataset.locale === 'en';
      const currency = cart.currency || this.dataset.currency;
      // A gift's own value must never unlock another gift. Only marked gift
      // lines are excluded; a customer's paid Aminasten remains eligible spend.
      const total = spend(cart);
      this.hidden = currency !== 'EUR';
      if (this.hidden) return;
      const money = cents => new Intl.NumberFormat(english ? 'en-IE' : 'bg-BG', {
        style: 'currency', currency: 'EUR', minimumFractionDigits: cents % 100 ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
      const selected = selection(cart);
      this.querySelectorAll('[data-gift-choice]').forEach(button => {
        const chosen = button.dataset.giftChoice === selected;
        button.setAttribute('aria-pressed', String(chosen));
        button.disabled = choicePending || button.dataset.available !== 'true';
        button.querySelector('.ama-tier-gifts__choice-label').textContent = button.dataset.available !== 'true'
          ? (english ? 'Unavailable' : 'Изчерпан') : chosen
            ? (total >= minimum() ? (english ? 'Selected · €0.00' : 'Избрано · €0,00') : (english ? 'Selected' : 'Избрано'))
            : (english ? 'Choose' : 'Избери');
      });
      this.querySelector('.ama-tier-gifts__next').textContent = total < minimum()
        ? (english ? 'Only ' + money(minimum() - total) + ' to your free gift' : 'Още ' + money(minimum() - total) + ' до безплатния подарък')
        : ((cart.items || []).some(item => isGift(item) && String(item.variant_id) === selected && item.final_line_price === 0)
          ? (english ? 'Your chosen gift is added free!' : 'Избраният подарък е добавен безплатно!') : (english ? 'Choose one free gift above.' : 'Избери един безплатен подарък по-горе.'));
      this.querySelector('.ama-tier-gifts__total').textContent = (english ? 'Your cart: ' : 'Твоята количка: ') + money(total);
      const percent = Math.min(100, total / minimum() * 100);
      const progress = this.querySelector('[role="progressbar"]');
      progress.setAttribute('aria-valuenow', String(Math.min(total, minimum()) / 100));
      progress.setAttribute('aria-valuetext', money(total) + ' / ' + money(minimum()));
      progress.firstElementChild.style.width = percent + '%';
    }
  }
  customElements.define('ama-tier-gifts', TierGifts);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  ['cart-drawer:refresh', 'cart-update', 'cart:updated', 'cart-drawer-open', 'shopify:section:load'].forEach(name => document.addEventListener(name, schedule));
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);
  if (configuration?.enabled) schedule();
  // The theme's own cart mutations render a fresh drawer, including custom
  // combo adds which don't emit pubsub. Observe only that region, not the page.
  const drawer = document.querySelector('cart-drawer');
  if (drawer) new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
})();
